import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiCalendar, FiClock, FiUser, FiX } from "react-icons/fi";
import type { Clip } from "../types";
import { formatDate } from "../utils";
import { BangerButton } from "./BangerButton";
import { DeleteClipButton } from "./DeleteClipButton";
import { ShareClipButton } from "./ShareClipButton";

interface VideoModalProps {
  clip: Clip;
  bangered: boolean;
  bangerBusy: boolean;
  isAdmin: boolean;
  deleting: boolean;
  onClose: () => void;
  onBanger: (clip: Clip) => void;
  onDelete: (clip: Clip) => void;
  onShare: (clip: Clip) => void;
}

export function VideoModal({ clip, bangered, bangerBusy, isAdmin, deleting, onClose, onBanger, onDelete, onShare }: VideoModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    document.body.classList.add("clip-modal-open");

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        const modal = closeButtonRef.current?.closest("[role=dialog]");
        const focusable = modal?.querySelectorAll<HTMLElement>("button, video[controls], [href], [tabindex]:not([tabindex='-1'])");
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("clip-modal-open");
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
      previousFocusRef.current?.focus();
    };
  }, [onClose]);

  return createPortal(
    <div className="clip-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="clip-modal" role="dialog" aria-modal="true" aria-labelledby="clip-modal-title">
        <button ref={closeButtonRef} type="button" className="clip-modal-close" onClick={onClose} aria-label="Close video player">
          <FiX aria-hidden="true" />
        </button>
        <div className="clip-player-shell">
          <video ref={videoRef} src={clip.videoUrl} controls preload="metadata" poster={clip.thumbnailUrl} />
        </div>
        <div className="clip-modal-details">
          <div className="clip-modal-heading">
            <h2 id="clip-modal-title">{clip.title}</h2>
            <div className="clip-modal-actions">
              <ShareClipButton placement="modal" onShare={() => onShare(clip)} />
              {isAdmin && (
                <DeleteClipButton
                  deleting={deleting}
                  placement="modal"
                  onDelete={() => onDelete(clip)}
                />
              )}
              <BangerButton
                count={clip.bangerCount}
                bangered={bangered}
                busy={bangerBusy}
                placement="modal"
                onBanger={() => onBanger(clip)}
              />
            </div>
          </div>
          {clip.description && <p>{clip.description}</p>}
          <dl>
            <div><dt><FiCalendar /> Recorded</dt><dd>{formatDate(clip.recordingDate)}</dd></div>
            <div><dt><FiClock /> Uploaded</dt><dd>{formatDate(clip.uploadedAt)}</dd></div>
            <div><dt><FiUser /> Uploader</dt><dd>{clip.uploaderName}</dd></div>
          </dl>
        </div>
      </section>
    </div>,
    document.body,
  );
}
